import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { useListUrlState } from '../../hooks/useListUrlState';
import { useDebounce } from '../../hooks/useDebounce';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { DraggableList } from '../common';
import CreateTestSuiteModal from '../test-suites/CreateTestSuiteModal';
import EditTestSuiteModal from '../test-suites/EditTestSuiteModal';
import RunTestSuiteModal from '../test-suites/RunTestSuiteModal';
import LinkTestCasesModal from '../test-suites/LinkTestCasesModal';
import {
  ConfirmDialog,
  DataTable,
  ErrorState,
  FilterBar,
  MetricCard,
  PageScaffold,
  Pagination,
  PriorityBadge,
  RowActions,
  SearchInput,
  StatusBadge,
  Breadcrumbs,
} from '../ui';
import { formatDate } from '../../utils/dateTimeFormat';

const LIST_SCHEMA = {
  page: 1,
  page_size: 20,
  search: '',
};

const suitesListStorageKey = (projectId) => `rt-suites-list:${projectId}`;

/**
 * BUILD · Test Suites — URL-persistent list with modern pagination & actions.
 */
const SuitesWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;
  const projectName = selectedProject?.name || 'Project';

  const {
    page,
    page_size: pageSize,
    search,
    setPage,
    setPageSize,
    setSearch,
    clearFilters,
    activeFilterCount,
    pageSizeOptions,
  } = useListUrlState(LIST_SCHEMA);

  const [searchDraft, setSearchDraft] = useState(search);
  const debouncedSearch = useDebounce(searchDraft, 350);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [reorderLoading, setReorderLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    const params = new URLSearchParams(location.search);
    if (params.has('page') || params.has('search')) return;
    const saved = sessionStorage.getItem(suitesListStorageKey(projectId));
    if (saved && saved !== location.search) {
      navigate({ pathname: location.pathname, search: saved }, { replace: true });
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!projectId || !location.search) return;
    sessionStorage.setItem(suitesListStorageKey(projectId), location.search);
  }, [projectId, location.search]);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch !== search) setSearch(debouncedSearch);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const [suites, setSuites] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editSuite, setEditSuite] = useState(null);
  const [runSuite, setRunSuite] = useState(null);
  const [linkSuite, setLinkSuite] = useState(null);
  const [deleteSuite, setDeleteSuite] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId) {
      setSuites([]);
      setLoading(false);
      setError('Select a project to manage suites.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const listParams = {
        page,
        page_size: pageSize,
        sort_by: 1,
        sort_order: 2,
      };
      if (search.trim()) listParams.search = search.trim();

      const [listRes, dashRes] = await Promise.all([
        axiosInstance.get(`/test_suite/list/${projectId}`, { params: listParams }),
        axiosInstance
          .get('/projects/suite_dashboard_data', {
            params: {
              page: 1,
              page_size: 10,
              workspace_id: getWorkspaceIdFromURL() || undefined,
              search: projectName || '',
            },
          })
          .catch(() => null),
      ]);

      const list = listRes?.test_suites || listRes?.data?.test_suites || [];
      setSuites(Array.isArray(list) ? list : []);
      const pages = listRes?.total_pages || listRes?.data?.total_pages || 1;
      setTotalPages(pages);
      setTotal(
        listRes?.total ||
          listRes?.total_count ||
          listRes?.data?.total ||
          (pages > 1 ? pages * pageSize : list.length)
      );

      if (dashRes) {
        setMetrics({
          suites: dashRes.total_suites_all_projects,
          cases: dashRes.total_test_cases_all_projects,
          passRate: dashRes.avg_pass_rate_all_projects,
          passed: dashRes.total_cases_passed_all_projects,
          failed: dashRes.total_cases_failed_all_projects,
          pending: dashRes.total_cases_pending_all_projects,
        });
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load suites');
      setSuites([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, pageSize, search, projectName, getWorkspaceIdFromURL]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suites;
    // Client-side refine when API returns unfiltered page
    const refined = suites.filter(
      (s) =>
        String(s.suite_name || s.name || '').toLowerCase().includes(q) ||
        String(s.description || '').toLowerCase().includes(q) ||
        String(s.status || '').toLowerCase().includes(q)
    );
    return refined.length ? refined : suites;
  }, [suites, search]);

  const openSuite = (suite) => {
    const id = suite.suite_id || suite.id;
    const listQs = location.search.replace(/^\?/, '');
    const ret = listQs ? `&ret=${encodeURIComponent(listQs)}` : '';
    navigateTo(`test-suites/${id}?project_id=${projectId}${ret}`);
  };

  const enterReorderMode = async () => {
    if (!projectId || reorderLoading) return;
    setReorderLoading(true);
    try {
      const all = [];
      let pg = 1;
      let pages = 1;
      do {
        const res = await axiosInstance.get(`/test_suite/list/${projectId}`, {
          params: { page: pg, page_size: 100, sort_by: 1, sort_order: 2 },
        });
        const list = res?.test_suites || res?.data?.test_suites || [];
        all.push(...(Array.isArray(list) ? list : []));
        pages = res?.total_pages || res?.data?.total_pages || 1;
        pg += 1;
      } while (pg <= pages && pg <= 50);

      setReorderItems(all.map((s) => ({ ...s, suite_id: s.suite_id || s.id })));
      setReorderMode(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load suites for reorder');
    } finally {
      setReorderLoading(false);
    }
  };

  const handleSuiteOrderChange = async (nextItems) => {
    const previous = reorderItems;
    setReorderItems(nextItems);
    try {
      const response = await axiosInstance.put('/test_suite/reorder_suites', {
        project_id: projectId,
        order: nextItems.map((s) => s.suite_id || s.id),
      });
      if (response?.status === 'success' || response?.status_code === 200) {
        toast.success(response?.message || 'Test suites reordered');
        setReorderMode(false);
        setReorderItems([]);
        await load();
      } else {
        throw new Error(response?.message || 'Failed to reorder suites');
      }
    } catch (err) {
      setReorderItems(previous);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to reorder');
    }
  };

  const handleExport = async () => {
    if (!projectId) return;
    setExporting(true);
    try {
      // Same sort as the list: created_at (1), descending (2)
      const params = { sort_by: 1, sort_order: 2 };
      if (search.trim()) params.search = search.trim();

      const blobData = await axiosInstance.get(`/test_suite/export/${projectId}`, {
        params,
        responseType: 'blob',
      });
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-suites-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteSuite) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/test_suite/${deleteSuite.suite_id || deleteSuite.id}`);
      setDeleteSuite(null);
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to delete suite');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'suite_name',
      header: 'Suite',
      render: (row) => (
        <div>
          <button
            type="button"
            className="rt2-link"
            onClick={(e) => {
              e.stopPropagation();
              openSuite(row);
            }}
          >
            {row.suite_name || row.name}
          </button>
          {row.description && (
            <div className="rt2-muted" style={{ marginTop: 2 }}>
              {String(row.description).slice(0, 80)}
              {String(row.description).length > 80 ? '…' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status || 'active'} />,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority || 'medium'} />,
    },
    {
      key: 'cases',
      header: 'Cases',
      render: (row) => row.test_case_count ?? row.total_test_cases ?? row.cases ?? '—',
    },
    {
      key: 'pass_rate',
      header: 'Pass Rate',
      render: (row) =>
        row.pass_rate != null || row.avg_pass_rate != null
          ? `${row.pass_rate ?? row.avg_pass_rate}%`
          : '—',
    },
    {
      key: 'updated',
      header: 'Last Updated',
      render: (row) =>
        formatDate(row.updated_at, '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 140,
      render: (row) => (
        <RowActions
          primary={
            <>
              <button
                type="button"
                className="rt-btn rt-btn--secondary rt-btn--sm"
                onClick={() => setRunSuite(row)}
                title="Run suite"
                aria-label={`Run ${row.suite_name || row.name}`}
              >
                Run
              </button>
              <button
                type="button"
                className="rt-btn rt-btn--tertiary rt-btn--sm"
                onClick={() => setLinkSuite(row)}
                title="Link test cases"
                aria-label={`Link cases to ${row.suite_name || row.name}`}
              >
                Link
              </button>
            </>
          }
          menuItems={[
            { id: 'view', label: 'View', onClick: () => openSuite(row) },
            { id: 'edit', label: 'Edit', onClick: () => setEditSuite(row) },
            {
              id: 'delete',
              label: 'Delete',
              description: row.executed
                ? 'This test suite cannot be deleted because it has already been executed'
                : undefined,
              danger: true,
              disabled: Boolean(row.executed),
              dividerBefore: true,
              onClick: () => setDeleteSuite(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageScaffold
      title="Test Suites"
      description="Organize reusable test collections and monitor their execution health."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Build' }, { label: 'Test Suites' }]} />}
      actions={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={handleExport}
            disabled={!projectId || exporting}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
          {reorderMode ? (
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={() => {
                setReorderMode(false);
                setReorderItems([]);
              }}
            >
              Done reordering
            </button>
          ) : (
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={enterReorderMode}
              disabled={!projectId || reorderLoading || !suites.length}
            >
              {reorderLoading ? 'Loading…' : 'Reorder'}
            </button>
          )}
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            onClick={() => setCreateOpen(true)}
            disabled={!projectId}
          >
            + Create Suite
          </button>
        </>
      }
    >
      <div className="rt-metric-grid" style={{ marginBottom: 4 }}>
        <MetricCard
          label="Suites"
          tone="primary"
          value={loading ? '…' : metrics?.suites ?? total ?? suites.length}
        />
        <MetricCard
          label="Linked Cases"
          tone="accent"
          value={loading ? '…' : metrics?.cases ?? '—'}
        />
        <MetricCard
          label="Pass Rate"
          tone="success"
          value={loading ? '…' : metrics?.passRate != null ? `${metrics.passRate}%` : '—'}
        />
        <MetricCard label="Failed" tone="danger" value={loading ? '…' : metrics?.failed ?? '—'} />
      </div>

      <FilterBar>
        <SearchInput value={searchDraft} onChange={setSearchDraft} placeholder="Search suites…" />
        {activeFilterCount > 0 && (
          <button
            type="button"
            className="rt-btn rt-btn--tertiary rt-btn--sm"
            onClick={() => {
              setSearchDraft('');
              clearFilters();
            }}
          >
            Clear
          </button>
        )}
      </FilterBar>

      {error && !suites.length && !loading && (
        <ErrorState
          title="Unable to load suites"
          description={
            error?.includes('Select a project')
              ? 'Please select a project before continuing.'
              : error
          }
          onRetry={load}
        />
      )}

      {reorderMode ? (
        <div className="rt-reorder-list">
          <p className="rt2-muted" style={{ marginBottom: 10, fontSize: 13 }}>
            Drag suites to reorder · saves automatically on drop
          </p>
          <DraggableList
            items={reorderItems}
            itemKey="suite_id"
            onReorder={handleSuiteOrderChange}
            renderItem={(suite) => (
              <div className="rt-reorder-row">
                <span className="rt-reorder-handle" aria-hidden>
                  ⋮⋮
                </span>
                <div className="rt-reorder-row__body">
                  <div className="rt-reorder-row__title">{suite.suite_name || suite.name}</div>
                  <div className="rt-reorder-row__meta">
                    <StatusBadge status={suite.status || 'active'} />
                    <span>
                      {suite.test_case_count ?? suite.total_test_cases ?? suite.cases ?? 0} cases
                    </span>
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey="suite_id"
            onRowClick={openSuite}
            loading={loading}
            emptyTitle="No suites found"
            emptyDescription="Create a suite, then link test cases."
          />

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={pageSizeOptions}
            loading={loading}
          />
        </>
      )}

      <CreateTestSuiteModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreateTestSuite={() => {
          setCreateOpen(false);
          load();
        }}
        projects={{ project_id: projectId, project_name: projectName }}
        defaultProjectId={projectId}
      />

      {editSuite && (
        <EditTestSuiteModal
          isOpen={Boolean(editSuite)}
          onClose={() => setEditSuite(null)}
          testSuite={editSuite}
          projects={[{ project_id: projectId, project_name: projectName }]}
          onSaveSuccess={() => {
            setEditSuite(null);
            load();
          }}
        />
      )}

      {runSuite && (
        <RunTestSuiteModal
          isOpen={Boolean(runSuite)}
          onClose={() => setRunSuite(null)}
          testSuiteId={runSuite.suite_id || runSuite.id}
          testSuiteName={runSuite.suite_name || runSuite.name}
          projectId={projectId}
          environmentId={runSuite.environment_id || runSuite.environment_setting_id}
          onSuccess={() => {
            setRunSuite(null);
            load();
          }}
        />
      )}

      {linkSuite && (
        <LinkTestCasesModal
          isOpen={Boolean(linkSuite)}
          onClose={() => setLinkSuite(null)}
          testSuite={linkSuite}
          projectId={projectId}
          mode="link"
          onSuccess={() => {
            setLinkSuite(null);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteSuite)}
        title="Delete suite?"
        body={`Delete "${deleteSuite?.suite_name || deleteSuite?.name}"? Linked cases are not deleted.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setDeleteSuite(null)}
        onConfirm={confirmDelete}
      />
    </PageScaffold>
  );
};

export default SuitesWorkspace;
